import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { kbAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Search, BookOpen, Eye } from 'lucide-react';

export default function KnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await kbAPI.search(searchQuery);
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to load articles', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadArticles();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold mb-2">Knowledge Base</h1>
          <p className="text-muted-foreground">Find answers to common questions and learn more about our services</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder="Search articles... (e.g., hosting, wordpress, email)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="kb-search-input"
                />
              </div>
              <Button type="submit" data-testid="kb-search-button">
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : articles.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No articles found</h3>
              <p className="text-muted-foreground">Try different search terms or browse all articles</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {articles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow" data-testid={`article-${article.id}`}>
                <CardHeader>
                  <CardTitle className="line-clamp-2">{article.title}</CardTitle>
                  <CardDescription className="flex items-center justify-between mt-2">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="flex items-center text-xs text-muted-foreground">
                      <Eye className="w-4 h-4 mr-1" />
                      {article.views} views
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {article.keywords.slice(0, 5).map((keyword, idx) => (
                      <span key={idx} className="bg-muted text-muted-foreground px-2 py-0.5 rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                  </div>
                  <Link to={`/knowledge-base/${article.id}`}>
                    <Button variant="outline" className="w-full" data-testid={`read-article-${article.id}`}>
                      Read Article
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Found {articles.length} article{articles.length !== 1 ? 's' : ''}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}